import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import SearchBar from "../../components/common/board/SearchFilterBar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import BoardToolbar from "../../components/common/board/BoardToolbar";

import { getAllMetaList } from "../../api/approval/meta";
import PageHeader from "../../components/common/ui/PageHeader";

export default function Docs() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // 검색 필터 — Permit 패턴 동일
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [status, setStatus] = useState("");
  const [module, setModule] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 여기 3개 필드만 Permit과 동일하게 가져오면 됨
  const SEARCH_FIELDS = [
    { value: "title", label: "제목" },
    { value: "dns", label: "DNS" },
    { value: "author", label: "작성자" },
  ];
  /** 서버 요청 — PermitDashboardPage 패턴 그대로 */
  const fetchList = async () => {
    setLoading(true);
    try {
      const params = {
        project: id,
        page,
        page_size: pageSize,
      };

      // SearchBar 조건 반영
      if (q) params[searchField] = q;

      if (dateFilter !== "all") {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      if (status) {
        params.status = status;
      }
      if (module) {
        params.module = module;
      }
      const data = await getAllMetaList(params);
      console.log(data);

      setRows(data?.results ?? []);
      setTotal(data?.total ?? 0);
    } finally {
      setLoading(false);
    }
  };

  /** useEffect — Permit과 동일 */
  useEffect(() => {
    if (!id) return;
    fetchList();
  }, [id, page, pageSize, searchTrigger]);

  /** 검색 버튼 이벤트 — Permit과 동일 */
  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const statusColor = (s) =>
    s === "approved"
      ? "bg-green-100 text-green-800"
      : s === "rejected"
      ? "bg-red-100 text-red-800"
      : s === "cancel"
      ? "bg-gray-100 text-gray-800"
      : s === "in_progress"
      ? "bg-blue-100 text-blue-800"
      : "bg-yellow-100 text-yellow-800";

  const statusText = (s) =>
    ({
      none:"상신",
      submit: "상신",
      in_progress: "진행중",
      approved: "승인",
      rejected: "반려",
      cancel: "취소",
    }[s] ||
    s ||
    "-");

  const moduleText = (s) =>
    ({
      approval: "결재",
      material: "자재",
      safety: "안전",
      quality: "품질",
      design: "설계",
      construction: "시공",
    }[s] ||
    s ||
    "-");

  const columns = [
    {
      key: "title",
      header: "제목",
      render: (row) => row?.title ?? "-",
    },
    {
      key: "doc_type",
      header: "구분",
      render: (row) => <span>{moduleText(row?.module)}</span>,
    },
    {
      key: "status",
      header: "상태",
      render: (row) => {
        const s = row?.status;
        return (
          <span className={`px-2 py-1 text-sm rounded-xl `}>
            {statusText(s)}
          </span>
        );
      },
    },
    {
      key: "author",
      header: "작성자",
      render: (row) =>
        row?.created_by?.name ??
        `${row?.created_by?.last_name ?? ""}${
          row?.created_by?.first_name ?? ""
        }`,
    },
    {
      key: "created_at",
      header: "작성일",
      render: (row) => (row?.created_at ? row.created_at.slice(0, 10) : "-"),
    },
  ];

  return (
    <div className="p-6 space-y-4">
      <PageHeader>전체 결재함</PageHeader>

      <BoardToolbar
        right={
          <>
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              searchFieldOptions={SEARCH_FIELDS}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
              leftExtra={
                <div className="flex items-center gap-2">
                  {/* 상태 */}
                  <select
                    className={`rounded-lg border px-3 py-2 text-sm bg-white `}
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      applySearch();
                    }}
                  >
                    <option value="">전체 상태</option>
                    <option value="submit">상신</option>
                    <option value="in_progress">진행중</option>
                    <option value="approved">승인</option>
                    <option value="rejected">반려</option>
                    <option value="cancel">취소</option>
                  </select>

                  {/* 모듈 */}
                  <select
                    className="rounded-lg border px-3 py-2 text-sm bg-white"
                    value={module}
                    onChange={(e) => {
                      setModule(e.target.value);
                      applySearch();
                    }}
                  >
                    <option value="">전체 모듈</option>
                    <option value="approval">결재</option>
                    <option value="material">자재</option>
                    <option value="safety">안전</option>
                    <option value="quality">품질</option>
                    <option value="design">설계</option>
                    <option value="construction">시공</option>
                  </select>
                </div>
              }
            />
            <button
              onClick={() => navigate(`/project/${id}/approval/request/new`)}
              className="btn-create"
            >
              결재상신
            </button>
          </>
        }
      />
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        keyField="id"
        onRowClick={(row) => {
          const module =
            row.module === "construction" ? "const" : row.module;

          navigate(
            `/project/${id}/${module}/${row.doc_type}/${row.dns}/view`
          );
        }}
      />

      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(next) => setPage(next)}
      />
    </div>
  );
}
